import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import clonedeep from 'lodash.clonedeep';
import {
  Box,
  Typography,
  AbsoluteBox,
  Backdrop,
  FlexBox,
  Checkbox,
  Stack,
  Button,
  color,
  MuiTable,
  Grid,
  MuiBox
} from '@kis-technologies/kis-ui';
import { Scrollbar } from 'components/Scrollbar';
import { Product, Item } from 'services/cart';
import {
  selectors as configSelectors,
  Configuration,
  EventDisplaySettings,
  LanguageField
} from 'services/configuration';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { formatCurrency, formatTitle } from 'utils/stringFormat';
import { getEventDisplaySettings } from 'views/Ticketing/utils';
import { useStyles } from './SeatDetailsOverlay.styled';
import { SeatDetailsOverlayProps } from './SeatDetailsOverlay.types';

const SeatDetailsOverlay = (props: SeatDetailsOverlayProps) => {
  const { t, i18n } = useTranslation();
  const { onCancel, onRemove, product, view } = props;

  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const removeTickets = get(config, `${ticketingPath}.cart.removeTickets.enabled`);
  const eventDisplaySettingsList: EventDisplaySettings[] = get(config, `${ticketingPath}.eventDisplaySettingsList`, []);
  const donationName = get(config, `${ticketingPath}.donations.productName.${i18n.language}`, t('donation.label'));
  const languageSettings: LanguageField[] = get(config, 'configuration.languageSettings', []);
  const cartItemLabel = get(config, `configuration.labels.cartItem.${i18n.language}`, t('cart.cartItemLabel'));
  const palette = get(config, 'configuration.globalSettings.palette');
  const removeTicketsHeader = get(
    config,
    `${ticketingPath}.cart.removeTickets.message.${i18n.language}`,
    t('cart.removeOverlayHeader', { items: formatTitle(cartItemLabel, i18n.language) })
  );
  const seatDetailsHeader = get(
    config,
    `${ticketingPath}.cart.seatDetailsOverlayMessage.${i18n.language}`,
    t('cart.seatDetails')
  );

  const [seatDetailsProduct, setSeatDetailsProduct] = useState<Product | null>(null);
  const [ticketsToRemove, setTicketsToRemove] = useState<Item[]>([]);

  const showRemoveOption = view === 'Remove' && removeTickets;
  const classes = useStyles();

  useEffect(() => {
    if (product) {
      setSeatDetailsProduct(clonedeep(product) as Product);
    }
  }, [product]);

  const getDateForDisplay = (p: Product) => {
    const { showDate } = getEventDisplaySettings(eventDisplaySettingsList, p.name);

    return showDate ? t('{{date, weekdayMonthDay}}', { date: new Date(p.date) }) : '';
  };

  const getTimeForDisplay = (p: Product) => {
    const { showTime } = getEventDisplaySettings(eventDisplaySettingsList, p.name);

    return showTime ? t('{{date, time}}', { date: new Date(p.date) }) : '';
  };

  const getRemoveButtonText = () => {
    let text = t('cart.remove');

    if (ticketsToRemove.length) {
      text += product.type && product.type === 'membership' ? ' ( 1 )' : ` ( ${ticketsToRemove.length} )`;
    }

    return text;
  };

  const isDateAndTimeDisplayed = (p: Product) => {
    const { showDate, showTime } = getEventDisplaySettings(eventDisplaySettingsList, p.name);

    return showDate && showTime;
  };

  const handleItemChecked = (checked: boolean, item: Item) => {
    if (checked) {
      setTicketsToRemove([...ticketsToRemove, item]);
    } else {
      const newList = ticketsToRemove.filter((listItem) => !(listItem.id === item.id));
      setTicketsToRemove(newList);
    }
  };

  const handleProductChecked = (checked: boolean) => {
    if (checked && seatDetailsProduct && seatDetailsProduct.items) {
      setTicketsToRemove(seatDetailsProduct.items);
    } else {
      setTicketsToRemove([]);
    }
  };

  const onClickRemove = () => {
    if (ticketsToRemove && ticketsToRemove.length) {
      onRemove(ticketsToRemove, (seatDetailsProduct as Product).type);
    }
  };

  const ItemRow = (rowProps: { item: Item; row: Product; subProduct?: Product; idx: number }) => {
    const { item, row, subProduct, idx } = rowProps;
    const productType = subProduct ? subProduct.type : row.type;
    const { showCartItemsPrice } = getEventDisplaySettings(eventDisplaySettingsList, row.name);

    return (
      <MuiTable.TableRow
        className={classes.seatDetailsRow}
        sx={
          ticketsToRemove.includes(item)
            ? { border: `3px solid ${palette?.primary}` }
            : { border: `3px solid transparent` }
        }
        onClick={() => showRemoveOption && handleItemChecked(!ticketsToRemove.includes(item), item)}
      >
        {showRemoveOption && (
          <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.cartRemoveCheckbox}`}>
            <FlexBox align="center" justify="center">
              <Checkbox
                size="medium"
                checked={ticketsToRemove.includes(item)}
                onChange={(e, checked) => handleItemChecked(checked, item)}
                data-testid={`remove-checkbox-${idx}`}
              />
            </FlexBox>
          </MuiTable.TableCell>
        )}
        <MuiTable.TableCell className={classes.tableBodyCell}>
          <MuiBox className={classes.cardEventTicketColumn}>
            <Typography fontSize={24} fontWeight={600} sx={{ lineHeight: 1.5 }}>
              {productType && productType === 'donation' ? donationName : item.type}
            </Typography>
          </MuiBox>
        </MuiTable.TableCell>
        <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.seatDetailsCell}`}>
          {item.seatInfo && !row.isGA && (
            <FlexBox direction="column" align="center">
              <Typography fontSize={18} fontWeight={400}>
                {t('commonText.section')} {item.seatInfo[0].section}
              </Typography>
              <Typography fontSize={18} fontWeight={400}>
                {t('commonText.row')} {item.seatInfo[0].row} &middot; {t('commonText.seat')} {item.seatInfo[0].seat}
              </Typography>
            </FlexBox>
          )}
        </MuiTable.TableCell>
        <MuiTable.TableCell className={`${classes.tableBodyCell}`}>
          <MuiBox className={classes.ticketPriceColumn}>
            <Typography fontSize={24} fontWeight={600} align="right" sx={{ lineHeight: 1.5 }}>
              {showCartItemsPrice && item.unitPrice ? formatCurrency(item.unitPrice, {}, languageSettings) : null}
            </Typography>
          </MuiBox>
        </MuiTable.TableCell>
      </MuiTable.TableRow>
    );
  };

  const ProductRow = (rowProps: { row: Product }) => {
    const { row } = rowProps;
    return (
      <MuiTable.TableRow
        className={classes.seatDetailsRow}
        sx={
          ticketsToRemove.length > 0 ? { border: `3px solid ${palette?.primary}` } : { border: `3px solid transparent` }
        }
        onClick={() => showRemoveOption && handleProductChecked(ticketsToRemove.length < 1)}
      >
        {showRemoveOption && (
          <MuiTable.TableCell className={`${classes.tableBodyCell} ${classes.cartRemoveCheckbox}`}>
            <FlexBox align="center" justify="center">
              <Checkbox
                size="medium"
                checked={ticketsToRemove.length > 0}
                onChange={(e, checked) => handleProductChecked(checked)}
                data-testid={`remove-product-checkbox`}
              />
            </FlexBox>
          </MuiTable.TableCell>
        )}
        <MuiTable.TableCell className={classes.tableBodyCell}>
          <MuiBox className={classes.cardEventTicketColumn}>
            <Typography fontSize={24} fontWeight={600} sx={{ lineHeight: 1.5 }}>
              {row.name.split(':')[1]}
            </Typography>
          </MuiBox>
        </MuiTable.TableCell>
        <MuiTable.TableCell className={`${classes.tableBodyCell}`}>
          <MuiBox className={classes.ticketPriceColumn}>
            <Typography fontSize={24} fontWeight={600} align="right" sx={{ lineHeight: 1.5 }}>
              {row.subTotal ? formatCurrency(row.subTotal, {}, languageSettings) : null}
            </Typography>
          </MuiBox>
        </MuiTable.TableCell>
      </MuiTable.TableRow>
    );
  };

  return (
    <Backdrop sx={{ background: 'rgba(255,255,255,0.95)' }} open>
      <Grid container className={classes.seatDetailsOverlayContainer}>
        <FlexBox flexOverride="1 0 auto" direction="column" padding="5" fullWidth>
          {/* Title */}
          <Box flexOverride="0 0 70px" margin="0 15">
            <Typography variant="h2" align="center" sx={{ lineHeight: 1 }}>
              {showRemoveOption ? removeTicketsHeader : seatDetailsHeader}
            </Typography>
          </Box>

          {/* Description */}
          <MuiBox className={classes.seatDetailsScrollContainer}>
            <Scrollbar refreshModel={seatDetailsProduct}>
              <Box flexOverride="none" padding="0 3" fullWidth>
                <MuiTable.TableContainer id="seatDetailsTable" className={classes.seatDetailsTableContainer}>
                  <MuiTable.Table className={classes.tableContainer} stickyHeader sx={{ borderCollapse: 'collapse' }}>
                    <>
                      {/* Package - list out the sub products */}
                      {seatDetailsProduct &&
                        seatDetailsProduct.subProducts &&
                        seatDetailsProduct.subProducts.length > 0 &&
                        seatDetailsProduct.subProducts.map((subProduct: Product, rowIdx: number) => {
                          return (
                            <MuiTable.TableBody key={rowIdx}>
                              <MuiBox className={classes.seatDetailsTableHead}>
                                <Typography fontSize={24} fontWeight={600}>
                                  {subProduct.name}
                                </Typography>
                                {seatDetailsProduct.firstDate && seatDetailsProduct.lastDate && subProduct.date && (
                                  <Typography fontSize={16}>
                                    {getDateForDisplay(subProduct)}
                                    {isDateAndTimeDisplayed(subProduct) ? <> &middot; </> : null}
                                    {getTimeForDisplay(subProduct)}
                                  </Typography>
                                )}
                              </MuiBox>
                              {subProduct.items &&
                                subProduct.items.map((item: Item, itemIdx: number) => {
                                  return (
                                    <ItemRow
                                      key={itemIdx}
                                      item={item}
                                      row={seatDetailsProduct}
                                      subProduct={subProduct}
                                      idx={itemIdx}
                                    />
                                  );
                                })}
                            </MuiTable.TableBody>
                          );
                        })}

                      {/* Membership - one row for the whole product */}
                      {seatDetailsProduct && seatDetailsProduct.type && seatDetailsProduct.type === 'membership' && (
                        <MuiTable.TableBody>
                          <MuiBox className={classes.seatDetailsTableHead}>
                            <Typography fontSize={24} fontWeight={600}>
                              {seatDetailsProduct.name.split(':')[0]}
                            </Typography>
                          </MuiBox>
                          <ProductRow row={seatDetailsProduct} />
                        </MuiTable.TableBody>
                      )}

                      {/* Event - one row for each ticket */}
                      {seatDetailsProduct &&
                        (!seatDetailsProduct.type || seatDetailsProduct.type !== 'membership') &&
                        seatDetailsProduct.items &&
                        seatDetailsProduct.items.length > 0 && (
                        <MuiTable.TableBody>
                          <MuiBox className={classes.seatDetailsTableHead}>
                            <Typography fontSize={24} fontWeight={600}>
                              {seatDetailsProduct.type && seatDetailsProduct.type === 'donation'
                                ? donationName
                                : seatDetailsProduct.name}
                            </Typography>
                          </MuiBox>
                          {seatDetailsProduct.items.map((item: Item, itemIdx: number) => {
                            return <ItemRow key={itemIdx} item={item} row={seatDetailsProduct} idx={itemIdx} />;
                          })}
                        </MuiTable.TableBody>
                      )}
                    </>
                  </MuiTable.Table>
                </MuiTable.TableContainer>
              </Box>
            </Scrollbar>
          </MuiBox>
        </FlexBox>

        {/* Cancel and Confirm Buttons */}
        <AbsoluteBox bottom="-45px">
          {showRemoveOption ? (
            <Stack spacing={12}>
              <Button
                data-testid="seat-details-cancel"
                customSize="x-large"
                customColor={color.BLACK}
                onClick={onCancel}
                appearance="white"
                className={classes.cancelButton}
              >
                {t('commonText.cancel')}
              </Button>

              <Button
                data-testid="remove-confirm"
                customSize="x-large"
                onClick={onClickRemove}
                appearance="primary"
                className={classes.confirmButton}
                disabled={!ticketsToRemove || !ticketsToRemove.length}
              >
                {getRemoveButtonText()}
              </Button>
            </Stack>
          ) : (
            <Button
              data-testid="seat-details-cancel"
              customSize="x-large"
              onClick={onCancel}
              appearance="primary"
              className={classes.cancelButton}
            >
              {t('commonText.close')}
            </Button>
          )}
        </AbsoluteBox>
      </Grid>
    </Backdrop>
  );
};

export default SeatDetailsOverlay;
